import { Box, Stack, Typography, useTheme } from '@mui/material';
import { DateTime } from 'luxon';
import { FC, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { dataTestIds } from 'src/constants/data-test-ids';
import { useChartData } from '../../../stores/appointment/appointment.store';

export const ProceduresContainer: FC = () => {
  const { chartData } = useChartData();
  const theme = useTheme();
  const { t } = useTranslation();
  const procedures = chartData?.procedures;

  const renderProperty = (label: string, value: string | undefined): ReactElement | undefined => {
    if (value == null) {
      return undefined;
    }
    return (
      <Box>
        <Typography display="inline" sx={{ fontWeight: '500' }}>
          {label}:
        </Typography>{' '}
        {value}
      </Box>
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, width: '100%' }}>
      <Typography variant="h5" color="primary.dark">
        {t('telemed.review.procedures.title')}
      </Typography>
      {procedures?.length ? (
        procedures.map((procedure) => (
          <Stack key={procedure.resourceId} data-testid={dataTestIds.progressNotePage.procedureItem}>
            <Typography sx={{ color: '#0F347C', fontWeight: '500' }}>{procedure.procedureType}</Typography>
            {renderProperty(
              t('telemed.review.procedures.cpt'),
              procedure.cptCodes != null && procedure.cptCodes.length > 0
                ? procedure.cptCodes.map((cptCode) => cptCode.code + ' ' + cptCode.display).join('; ')
                : undefined
            )}
            {renderProperty(
              t('telemed.review.procedures.dx'),
              procedure.diagnoses != null && procedure.diagnoses.length > 0
                ? procedure.diagnoses.map((diagnosis) => diagnosis.code + ' ' + diagnosis.display).join('; ')
                : undefined
            )}
            {renderProperty(
              t('telemed.review.procedures.dateTime'),
              procedure.procedureDateTime != null
                ? DateTime.fromISO(procedure.procedureDateTime).toFormat('MM/dd/yyyy, HH:mm a')
                : undefined
            )}
            {renderProperty(t('telemed.review.procedures.performedBy'), procedure.performerType)}
            {renderProperty(t('telemed.review.procedures.anaesthesia'), procedure.medicationUsed)}
            {renderProperty(t('telemed.review.procedures.site'), procedure.bodySite)}
            {renderProperty(t('telemed.review.procedures.side'), procedure.bodySide)}
            {renderProperty(t('telemed.review.procedures.technique'), procedure.technique)}
            {renderProperty(t('telemed.review.procedures.instruments'), procedure.suppliesUsed)}
            {renderProperty(t('telemed.review.procedures.details'), procedure.procedureDetails)}
            {renderProperty(
              t('telemed.review.procedures.specimenSent'),
              procedure.specimenSent != null
                ? procedure.specimenSent
                  ? t('patientProfile.text.yes')
                  : t('patientProfile.text.no')
                : undefined
            )}
            {renderProperty(t('telemed.review.procedures.complications'), procedure.complications)}
            {renderProperty(t('telemed.review.procedures.patientResponse'), procedure.patientResponse)}
            {renderProperty(t('telemed.review.procedures.postInstructions'), procedure.postInstructions)}
            {renderProperty(t('telemed.review.procedures.timeSpent'), procedure.timeSpent)}
            {renderProperty(t('telemed.review.procedures.documentedBy'), procedure.documentedBy)}
          </Stack>
        ))
      ) : (
        <Typography color={theme.palette.text.secondary}>{t('telemed.review.procedures.noData')}</Typography>
      )}
    </Box>
  );
};
